import type { CSSProperties, FC, ReactNode } from 'react'

import Image from 'next/image'
import { Eye, Handshake, Headphones, ShieldCheck } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LogoMark } from '@/components/generals/logo'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { RevealText } from '@/components/generals/reveal-text'
import { PageHero } from '@/components/sections/page-hero'
import { AboutStatement } from '@/components/landing/about-statement'

const PILLARS = [
    {
        number: '01',
        title: 'Mission',
        body: "Give every company a finance function that runs in real time, so leaders decide from current, trusted numbers instead of last month's spreadsheet."
    },
    {
        number: '02',
        title: 'Vision',
        body: 'A world where closing the books is a non-event, forecasts update themselves and every team understands how its choices move the business.'
    },
    {
        number: '03',
        title: 'Approach',
        body: 'Automate the repetitive work end to end, explain every number in plain language and keep a person in the loop for every judgment call.'
    }
]

const PRINCIPLES = [
    {
        title: 'Clarity over complexity',
        body: 'Financial software should make the picture simpler. Every screen we design answers one question well, and nothing more.'
    },
    {
        title: 'Automation with accountability',
        body: 'AI handles the repetitive work. Every action it takes is explainable, reversible and recorded, so trust is earned, not assumed.'
    },
    {
        title: 'Security as the foundation',
        body: 'Read-only connections, encryption everywhere and private-by-default data are not features we sell. They are the floor we build on.'
    },
    {
        title: 'Built alongside finance teams',
        body: 'Controllers, CFOs and founders shape our roadmap every week. We ship what removes real work from real closes.'
    },
    {
        title: 'Partners for the long run',
        body: 'We measure success by the hours we give back to your team, quarter after quarter, not by how many dashboards we add.'
    }
]

const COMMITMENTS = [
    {
        title: 'Your data stays yours',
        body: 'Financial data is never sold and never used to train models for other customers.',
        Icon: ShieldCheck
    },
    {
        title: 'Every automation is explainable',
        body: 'See why the AI made each decision, change it and undo it at any time.',
        Icon: Eye
    },
    {
        title: 'Transparent, predictable pricing',
        body: 'Clear plans, no surprise fees and no long contracts required to get started.',
        Icon: Handshake
    },
    {
        title: 'Real people when you need them',
        body: 'Finance specialists answer support, not scripts. Enterprise teams get a dedicated manager.',
        Icon: Headphones
    }
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description='Veyra is building the calm center of company finance: AI that automates the busywork, explains every number and keeps finance teams in control.'
            />

            <PageHero
                eyebrow='About Veyra'
                titleSize='lg'
                title={<>We&rsquo;re building the calm center of <span className='text-stone'>company finance.</span></>}
                description='AI that handles the finance busywork, so your team can focus on decisions while staying firmly in control.'
                actions={(
                    <>
                        <ButtonLink href='/demo' size='lg' arrow>Meet the product</ButtonLink>
                        <ButtonLink href='/contact' variant='secondary' size='lg'>Get in touch</ButtonLink>
                    </>
                )}
                aside={(
                    <figure className='relative'>
                        <div className='relative aspect-[4/5] overflow-hidden rounded-[28px] bg-cream-dark sm:aspect-[5/4] lg:aspect-[4/5]'>
                            <Image
                                src='/images/about/team-meeting.png'
                                alt='Four colleagues in light blazers talking around a white meeting table in a bright office'
                                fill
                                priority
                                sizes='(min-width: 1024px) 45vw, 100vw'
                                className='object-cover object-[50%_60%]'
                            />
                        </div>

                        <figcaption className='absolute -bottom-6 left-6 flex items-center gap-3 rounded-2xl border border-burgundy/10 bg-ivory/95 px-4 py-3 shadow-[0_24px_50px_-24px_rgb(57_26_24/0.5)] backdrop-blur sm:left-8'>
                            <span aria-hidden='true' className='flex size-8 items-center justify-center rounded-md bg-[#F26522] text-sm font-bold text-white'>Y</span>
                            <span>
                                <span className='block text-sm font-medium text-burgundy'>Backed by Y Combinator</span>
                                <span className='block text-xs text-stone'>Built in San Francisco</span>
                            </span>
                        </figcaption>
                    </figure>
                )}
            />

            <section aria-labelledby='purpose-title' className='bg-cream'>
                <Container className='py-28 md:py-36'>
                    <div className='max-w-3xl'>
                        <Eyebrow>Why we exist</Eyebrow>

                        <RevealText
                            as='h2'
                            id='purpose-title'
                            text='Numbers should help you decide, not slow you down.'
                            highlight={['help you decide,']}
                            highlightClassName='text-stone'
                            className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                        />
                    </div>

                    <div className='mt-16 grid border-t border-burgundy/10 md:grid-cols-3'>
                        {PILLARS.map((pillar, index) => (
                            <article
                                key={pillar.title}
                                data-reveal=''
                                className='border-b border-burgundy/10 py-10 md:border-b-0 md:px-8 md:py-12 md:first:pl-0 md:[&:not(:first-child)]:border-l'
                                style={{ '--reveal-delay': `${index * 120}ms` } as CSSProperties}
                            >
                                <p className='font-mono text-xs text-stone'>{pillar.number}</p>
                                <h3 className='mt-6 font-heading text-display-sm font-medium text-burgundy'>{pillar.title}</h3>
                                <p className='mt-4 leading-relaxed text-stone-dark'>{pillar.body}</p>
                            </article>
                        ))}
                    </div>
                </Container>
            </section>

            <AboutStatement eyebrow='Our impact' showLink={false} />

            <section aria-labelledby='principles-title' className='bg-paper'>
                <Container className='py-28 md:py-36'>
                    <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                        <div className='lg:col-span-4'>
                            <div className='lg:sticky lg:top-28'>
                                <Eyebrow>What we believe</Eyebrow>

                                <RevealText
                                    as='h2'
                                    id='principles-title'
                                    text='Five principles behind every decision we make.'
                                    className='mt-6 font-heading text-display-md font-medium text-burgundy'
                                />
                            </div>
                        </div>

                        <ol className='border-t border-burgundy/10 lg:col-span-7 lg:col-start-6'>
                            {PRINCIPLES.map((principle, index) => (
                                <li
                                    key={principle.title}
                                    data-reveal=''
                                    className='grid gap-4 border-b border-burgundy/10 py-9 sm:grid-cols-[80px_1fr]'
                                    style={{ '--reveal-delay': `${index * 60}ms` } as CSSProperties}
                                >
                                    <span className='font-mono text-xs text-stone'>{String(index + 1).padStart(2, '0')}</span>

                                    <div>
                                        <h3 className='text-xl font-medium text-burgundy'>{principle.title}</h3>
                                        <p className='mt-3 max-w-xl leading-relaxed text-stone-dark'>{principle.body}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </Container>
            </section>

            <section aria-labelledby='commitment-title' className='relative overflow-hidden bg-cream'>
                <LogoMark className='pointer-events-none absolute -left-32 top-1/2 size-[520px] -translate-y-1/2 text-burgundy/[0.035]' />

                <Container className='relative py-28 md:py-36'>
                    <div className='grid gap-14 lg:grid-cols-12 lg:items-start lg:gap-10'>
                        <div className='lg:col-span-5'>
                            <Eyebrow>Our commitment</Eyebrow>

                            <RevealText
                                as='h2'
                                id='commitment-title'
                                text='Simplifying finance, responsibly.'
                                highlight={['responsibly.']}
                                highlightClassName='text-stone'
                                className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                            />

                            <p data-reveal='' className='mt-6 max-w-md text-lg leading-relaxed text-stone-dark'>
                                AI is only useful in finance if you can trust it. These are the promises we make to every customer, from a two-person startup to a global enterprise.
                            </p>
                        </div>

                        <ul className='grid gap-4 sm:grid-cols-2 lg:col-span-7'>
                            {COMMITMENTS.map(({ title, body, Icon }, index) => (
                                <li
                                    key={title}
                                    data-reveal=''
                                    className='rounded-3xl border border-burgundy/10 bg-ivory/70 p-7'
                                    style={{ '--reveal-delay': `${index * 90}ms` } as CSSProperties}
                                >
                                    <Icon aria-hidden='true' className='size-5 text-gold-dark' />
                                    <h3 className='mt-6 text-lg font-medium text-burgundy'>{title}</h3>
                                    <p className='mt-2 leading-relaxed text-stone-dark'>{body}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export default About
